/**
 * Bepaalt welk contact gebruikt wordt om de voorbeeldmail (en de voorbeeldwaarden
 * bij de dynamische velden) te tonen.
 *
 * Regel: zodra er minstens één ontvanger mét bruikbaar e-mailadres geselecteerd is,
 * wordt dat echte contact getoond — nooit de verzonnen voorbeeldpersoon. De
 * voorbeeldpersoon ("Jan Janssen") is enkel een terugvaloptie wanneer er nog geen
 * enkele geldige ontvanger is; anders denkt de gebruiker dat de mail naar Jan
 * Janssen vertrekt.
 *
 * Alle componenten die een voorbeeld tonen (EmailPreview, DynamicFieldsManager,
 * PlaceholderPreview) gebruiken deze functie, zodat ze altijd hetzelfde contact
 * laten zien.
 */
import { Contact } from '../types';
import { hasUsableEmail } from './recipientTypes';

/** Id van de verzonnen voorbeeldpersoon; ook de initiële waarde in de state. */
export const DUMMY_PREVIEW_CONTACT_ID = 'dummy';

/** Verzonnen contact, enkel gebruikt als er nog geen geldige ontvanger is. */
export const dummyPreviewContact: Contact = {
  id: DUMMY_PREVIEW_CONTACT_ID,
  name: 'Jan Janssen (voorbeeld)',
  resourceId: 'RES-2024-001',
  firstName: 'Jan',
  lastName: 'Janssen',
  phoneMobile: '+32 123 456 789',
  email: 'jan.janssen@example.be',
  buildingName: 'Gebouw A',
  unitNumber: '101',
  units: [],
};

/** Contacten die effectief een mail kunnen krijgen, dus bruikbaar als voorbeeld. */
export const getPreviewableContacts = (contacts?: Contact[]): Contact[] =>
  (contacts ?? []).filter((contact) => !!contact && hasUsableEmail(contact));

export interface ResolvedPreviewContact {
  /** Contact waarmee de placeholders gevuld worden. */
  contact: Contact;
  /** True wanneer er geen geldige ontvanger is en dus de voorbeeldpersoon getoond wordt. */
  isDummy: boolean;
  /** Ontvangers waaruit de gebruiker kan kiezen in de voorbeeld-selector. */
  previewableContacts: Contact[];
}

/**
 * Kiest het voorbeeldcontact: de expliciete keuze van de gebruiker, anders de
 * eerste geldige ontvanger, en enkel bij gebrek daaraan de voorbeeldpersoon.
 */
export const resolvePreviewContact = (
  contacts: Contact[] | undefined,
  selectedPreviewContactId?: string,
): ResolvedPreviewContact => {
  const previewableContacts = getPreviewableContacts(contacts);

  const explicitChoice =
    selectedPreviewContactId && selectedPreviewContactId !== DUMMY_PREVIEW_CONTACT_ID
      ? previewableContacts.find(
          (contact) => String(contact.id) === String(selectedPreviewContactId),
        )
      : undefined;

  const contact = explicitChoice ?? previewableContacts[0];

  return contact
    ? { contact, isDummy: false, previewableContacts }
    : { contact: dummyPreviewContact, isDummy: true, previewableContacts };
};
